import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Icon, IconButton, Stack, Tooltip } from '@mui/material';
import { type GridRowId, type GridRowsProp , type GridColDef } from '@mui/x-data-grid';
import { type FC, useMemo } from 'react';

import CodeDelete from './CodeDelete.js';
import CodeForm from './CodeForm.js';
import { useCheckPermissions } from '../../auth/index.js';
import useDialogControls from '../../hooks/use-dialog-controls.js';
import { type Code } from '../../models/index.js';
import Table from '../Table/Table.js';
import tableActions from '../Table/tableActions.js';


interface CodesTableProps {
  data: Code[];
  isLoading: boolean;
  refetch: () => void;
  selectedRow?: Code;
  setSelectedRow: (code: Code | undefined) => void;
}

const CodesTable: FC<CodesTableProps> = ({ data, refetch, isLoading, selectedRow, setSelectedRow }) => {
  const {open: formOpen, selectedData: formData, onOpen: onFormOpen, reset: resetForm, setOpen: setFormOpen} = useDialogControls<Code>(data);
  const {open: deleteOpen, selectedData: deleteData, onOpen: onDelete, reset: resetDelete} = useDialogControls<Code>(data);

  const { canCreateCode, canUpdateCode, canDeleteCode } = useCheckPermissions();

  const columns: GridColDef[] = useMemo(() => {
    const cols: GridColDef[] = [
      { field: 'code', headerName: 'Code', flex: 1 },
      {
        field: 'description',
        headerName: 'Description',
        flex: 1,
      },
      { field: 'category', headerName: 'Category', flex: 1 },
      { field: 'metadata', headerName: 'Metadata', width: 100, type: 'boolean' },
    ];
    if (canUpdateCode || canDeleteCode) {
      cols.push({
        field: 'actions',
        headerName: 'Actions',
        width: 100,
        sortable: false,
        type: 'actions',
        getActions: (params) => tableActions(onDelete(params.id), onFormOpen(params.id))({
          canEdit: canUpdateCode,
          canDelete: canDeleteCode,
        }),
      });
    }
    return cols;
  }, [canUpdateCode, canDeleteCode, onFormOpen, onDelete]);

  const rows: GridRowsProp = useMemo(
    () =>
      data.map((item) => ({
        id: item.id,
        code: item.code,
        description: item.description,
        category: item.category?.name,
        metadata: !!item.metadata?.length,
      })),
    [data],
  );

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <Stack direction={'row'} justifyContent={'space-between'} sx={{ my: 1 }}>
        <Stack direction={'row'} alignItems={'center'} spacing={1}>
          <h2 style={{ margin: 0 }}>Codes</h2>
          <Tooltip
            arrow
            color="primary"
            placement="bottom"
            title={
              'To access the metadata of a certain code, select a row by clicking it. To unselect a row, hold the "Ctrl" ("Cmd" on MacOS) key and click on it.'
            }
          >
            <Icon>
              <InfoOutlinedIcon />
            </Icon>
          </Tooltip>
        </Stack>
        {canCreateCode ? (
          <Tooltip arrow color="primary" placement="bottom" title={'Add a new code'}>
            <IconButton color="inherit" aria-label="Add a new code" onClick={() => setFormOpen(true)} >
              <AddIcon />
            </IconButton>
          </Tooltip>
        ) : null}
      </Stack>
      <Table
        columns={columns}
        rows={rows}
        NoRowsOverlayText={'No codes available.'}
        isLoading={isLoading}
        disableRowSelectionOnClick={false}
        onRowSelectionModelChange={(newRowSelectionModel) => {
          setSelectedRow(data.find((item) => item.id === newRowSelectionModel[0]));
        }}
        rowSelectionModel={selectedRow?.id ? [selectedRow?.id as GridRowId] : []}
      />
      <CodeForm
        open={formOpen}
        onClose={resetForm}
        code={formData}
        refetch={refetch}
      />
      <CodeDelete
        open={deleteOpen}
        onClose={resetDelete}
        code={deleteData}
        refetch={refetch}
      />
    </Box>
  );
};

export default CodesTable;
